import axios, { AxiosHeaders, type AxiosInstance } from 'axios'
import { inject, type InjectionKey } from 'vue'
import type { ApiError } from '@yuksalish/domain-types'

export const apiClientKey: InjectionKey<AxiosInstance> =
  Symbol('yuksalish-api-client')

export interface ApiClientOptions {
  baseURL: string
  getAccessToken?: () => string | null
  onUnauthorized?: () => void
}

export function createApiClient(options: ApiClientOptions): AxiosInstance {
  const client = axios.create({
    baseURL: options.baseURL,
    headers: { Accept: 'application/json' },
  })

  client.interceptors.request.use((config) => {
    const token = options.getAccessToken?.()
    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }
    return config
  })

  client.interceptors.response.use(
    (response) => response,
    (error: unknown) => {
      if (axios.isAxiosError(error) && error.response?.status === 401) {
        options.onUnauthorized?.()
      }
      return Promise.reject(error)
    },
  )

  return client
}

export function normalizeApiError(error: unknown): ApiError {
  if (!axios.isAxiosError(error)) {
    return { message: 'An unexpected error occurred.' }
  }

  const responseData = error.response?.data as
    | {
        message?: string
        error?: { message?: string; code?: string }
        errors?: Record<string, string[]>
      }
    | undefined

  const responseHeaders = error.response?.headers
  const readHeader = (name: string): unknown => {
    if (responseHeaders instanceof AxiosHeaders) {
      return responseHeaders.get(name)
    }
    return Object.entries(responseHeaders ?? {}).find(
      ([key]) => key.toLowerCase() === name,
    )?.[1]
  }
  const correlationId = [
    readHeader('x-correlation-id'),
    readHeader('x-request-id'),
  ].find((value): value is string => typeof value === 'string' && value !== '')

  return {
    message:
      responseData?.error?.message ??
      responseData?.message ??
      error.message ??
      'The request failed.',
    code: responseData?.error?.code,
    errors: responseData?.errors,
    correlationId,
  }
}

export function formatApiError(error: unknown): string {
  const normalized = normalizeApiError(error)
  return normalized.correlationId
    ? `${normalized.message} Reference: ${normalized.correlationId}`
    : normalized.message
}

export function useApiClient(): AxiosInstance {
  const client = inject(apiClientKey)
  if (!client) {
    throw new Error('API client has not been provided.')
  }
  return client
}
